import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import { useAuthStore } from '#/stores/auth';
import { useNotificationStore } from '#/stores/notification';

/**
 * 일부 요청은 에러를 "정상 흐름"으로 취급해야 한다(예: 부팅 시 세션 확인 — 미로그인 401 은 기대된 응답).
 * 이 플래그가 true 인 요청은 인터셉터가 전역 토스트·clearSession 을 건너뛰고 에러만 그대로 전파한다.
 */
export interface SilentRequestConfig extends InternalAxiosRequestConfig {
  /** true 면 응답 에러를 사용자 알림 없이 조용히 전파(호출부가 직접 분기 처리). */
  silent?: boolean;
  /**
   * 일부 상태만 "기대된 흐름"으로 토스트를 건너뛴다(나머지 상태는 평소대로 전역 토스트 1회).
   * 예: 상세 조회의 404 = 비노출 not-found(호출부가 null 로 흡수). 401·500 은 그대로 알림.
   * `silent: true`(전체 무음)와 달리 **선택 상태만** 무음 처리한다.
   */
  silentStatuses?: number[];
}

/**
 * axios 단일 인스턴스 + 전역 에러 인터셉터.
 *
 * 에러 처리 일원화(MIGRATION_PROMPT §4-7):
 *  - 응답 에러는 **여기서 1회만** 사용자 메시지로 변환해 전역 토스트로 노출한다.
 *  - 컴포넌트 catch 에서 message.error/alert 를 직접 호출하지 않는다(중복 에러 알림 안티패턴).
 *  - 인터셉터는 토스트를 띄운 뒤 에러를 **그대로 reject** 한다 → 호출부는 로딩 종료·폼 상태
 *    복구 같은 흐름 제어만 하고, 알림은 띄우지 않는다.
 *
 * 현재 api/*.ts 는 stub(Promise.resolve) 라 실제 네트워크 에러는 발생하지 않는다.
 * 실서버 연동 시 stub 본문을 http 호출로 교체하면 이 인터셉터가 자동 적용된다(시그니처 동일).
 */

/** HTTP 상태코드 → 사용자 표시 문구(서버 message 가 있으면 우선). */
const STATUS_MESSAGE: Record<number, string> = {
  400: '요청 내용을 다시 확인해 주세요.',
  401: '로그인이 필요하거나 인증이 만료되었습니다.',
  403: '접근 권한이 없습니다.',
  404: '요청한 정보를 찾을 수 없습니다.',
  409: '이미 처리된 요청이거나 중복된 데이터입니다.',
  422: '입력값을 다시 확인해 주세요.',
  429: '요청이 많습니다. 잠시 후 다시 시도해 주세요.',
  500: '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
  502: '서버에 연결하지 못했습니다. 잠시 후 다시 시도해 주세요.',
  503: '서비스가 일시적으로 원활하지 않습니다. 잠시 후 다시 시도해 주세요.',
};

/** 기본(상태 미상) 문구. */
const FALLBACK_MESSAGE = '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';
/** 네트워크 단절·타임아웃 등 응답 자체가 없을 때. */
const NETWORK_MESSAGE = '네트워크 연결을 확인해 주세요.';

/** 서버가 내려준 본문에서 사람이 읽을 메시지를 안전하게 추출(없으면 null). */
function extractServerMessage(data: unknown): string | null {
  if (data == null || typeof data !== 'object') return null;
  const record = data as Record<string, unknown>;
  const candidate = record.message ?? record.error ?? record.detail;
  return typeof candidate === 'string' && candidate.trim() !== '' ? candidate : null;
}

/** AxiosError → 사용자 표시 메시지로 변환. */
function resolveErrorMessage(error: AxiosError): string {
  if (error.response) {
    const { status, data } = error.response;
    return extractServerMessage(data) ?? STATUS_MESSAGE[status] ?? FALLBACK_MESSAGE;
  }
  // 응답 없음: 네트워크 오류·타임아웃·요청 취소.
  return NETWORK_MESSAGE;
}

/**
 * 응답 에러가 특정 HTTP 상태인지 판별(타입 가드 헬퍼).
 *
 * API 모듈이 "기대된 에러 상태"(예: 상세 조회 404 = 비노출 not-found)를 흐름 분기할 때 사용한다.
 * `axios` 직접 import 를 http 레이어 한 곳(SSoT)에 가둬, 도메인 API 모듈이 axios 에 직접 의존하지 않게 한다.
 */
export function isHttpStatus(error: unknown, status: number): boolean {
  return axios.isAxiosError(error) && error.response?.status === status;
}

/**
 * 세션 만료(401) 시점의 복귀 부적합 경로 — 인증 흐름·오류 화면 자체는 재로그인 후 돌아갈 곳이 아니다.
 * (직접 진입 차단 경로는 라우터 가드가 `redirect` 쿼리로 별도 보존한다 — 여기는 "보던 중 만료" 경로 전용.)
 */
const RETURN_URL_EXCLUDED_PREFIXES = ['/login', '/signup', '/auth/', '/error/'];

/**
 * 세션 만료 복귀지로 보존할 현재 화면 경로를 반환한다(부적합하면 null).
 * http 레이어에는 Router 인스턴스가 없으므로(ViteSSG 가 생성) webHistory URL 을 직독한다.
 */
function currentPathForReturn(): string | null {
  // SSG 빌드(브라우저 아님) 방어 — 프리렌더 중에는 복귀지 개념이 없다.
  if (typeof window === 'undefined') return null;
  const { pathname, search, hash } = window.location;
  if (RETURN_URL_EXCLUDED_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return null;
  return pathname + search + hash;
}

export const http: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '/api',
  timeout: 10_000,
  // HttpOnly 세션 쿠키(IPSI_SESSION)를 자동 첨부 — FR-017/FR-018.
  // 백엔드 CORS 는 credentials:true + 명시적 Origin 허용(와일드카드 불가)으로 구성해야 한다.
  withCredentials: true,
});

http.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    // 요청 취소(컴포넌트 unmount 등)는 사용자 에러가 아니므로 알림 없이 통과.
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    if (axios.isAxiosError(error)) {
      // silent 요청(예: 부팅 세션 확인)은 에러가 기대된 흐름 — 전역 토스트·상태 정리를 건너뛴다.
      // 호출부가 직접 분기(미로그인 처리)하므로 에러만 그대로 전파한다.
      const config = error.config as SilentRequestConfig | undefined;
      const status = error.response?.status;
      // 전체 무음(silent) 또는 선택 상태 무음(silentStatuses)에 해당하면 토스트를 건너뛴다.
      // 예: 상세 조회 404 = 기대된 not-found(호출부가 null 흡수) — 빈 상태와 토스트 동시 노출 방지.
      const silent =
        config?.silent === true ||
        (status != null && config?.silentStatuses?.includes(status) === true);
      if (!silent) {
        // Pinia 스토어는 호출 시점(런타임)에 접근 — 모듈 평가 시점 미초기화 회피.
        useNotificationStore().pushError(resolveErrorMessage(error));

        // 401: 세션 만료·인증 없음 → 클라이언트 로컬 상태 초기화(쿠키는 백엔드가 소유·관리).
        // clearSession→logout 이 인증 수준·장바구니·vue-query 캐시까지 일괄 정리한다(로그아웃 SSoT).
        // 토스트는 위에서 이미 발행했으므로 여기서는 상태만 정리한다(중복 알림 없음).
        if (error.response?.status === 401) {
          const auth = useAuthStore();
          // 복귀지를 세션 정리 **전에** 판정한다 — clearSession(→logout)이 기존 returnUrl 을 폐기하므로.
          // 현재 화면이 복귀 부적합(예: 로그인 화면에서 비밀번호 오류 401)이면 기존 복귀지를 승계해
          // 만료 직전 보존된 "보던 화면"이 로그인 재시도 한 번에 유실되지 않게 한다.
          const returnPath = currentPathForReturn() ?? auth.returnUrl;
          auth.clearSession();
          // 재보존 → 재로그인 후 홈이 아니라 보던 화면으로 복귀.
          // 명시적 로그아웃(useLogout)은 이 경로를 타지 않아 의도적 이탈에는 복귀지가 남지 않는다.
          if (returnPath) auth.setReturnUrl(returnPath);
        }
      }
    }

    // 알림은 띄웠으므로(또는 silent) 호출부는 흐름 제어만. 에러는 그대로 전파한다.
    return Promise.reject(error);
  },
);
